import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * DW Cloud OAuth callback page.
 * DW Cloud redirects the popup here with ?code=xxx.
 * We call the backend to exchange the code for tokens, then postMessage the result to the opener.
 */
export default function DWOAuthCallback() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState('')
  const [systemCount, setSystemCount] = useState(0)

  useEffect(() => {
    const code = searchParams.get('code')
    if (!code) {
      setStatus('error')
      setError('No authorization code received')
      if (window.opener) {
        window.opener.postMessage({ type: 'dw_oauth_error', error: 'No authorization code received' }, '*')
      }
      return
    }

    // Exchange code for tokens via public backend endpoint
    fetch('/api/public/dw/oauth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string }
          throw new Error(body.error || `Exchange failed: ${res.status}`)
        }
        return res.json() as Promise<{ access_token: string; refresh_token: string; systems: { id: string; name: string; stateOfHealth: string }[] }>
      })
      .then((data) => {
        setStatus('success')
        setSystemCount(data.systems?.length || 0)
        if (window.opener) {
          window.opener.postMessage({
            type: 'dw_oauth_callback',
            payload: {
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              systems: data.systems,
            },
          }, '*')
          setTimeout(() => window.close(), 1500)
        }
      })
      .catch((err: Error) => {
        setStatus('error')
        setError(err.message)
        if (window.opener) {
          window.opener.postMessage({ type: 'dw_oauth_error', error: err.message }, '*')
          setTimeout(() => window.close(), 3000)
        }
      })
  }, [searchParams])

  return (
    <div style={{
      background: 'var(--bg-0, #0f0f17)',
      color: 'var(--text-1, #f1f5f9)',
      fontFamily: 'system-ui, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      margin: 0,
    }}>
      <div style={{ textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21,12a9,9,0,1,1-6.219-8.56" />
              </svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Connecting to DW Cloud...</div>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Exchanging authorization code</div>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>&#x2705;</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>DW Cloud Connected</div>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>
              Found {systemCount} system{systemCount !== 1 ? 's' : ''}. Returning to portal...
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>&#x274C;</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Connection Failed</div>
            <div style={{ fontSize: 13, color: '#94a3b8', maxWidth: 300 }}>{error}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 16 }}>You can close this window.</div>
          </>
        )}
      </div>
    </div>
  )
}
